/** Unit registry. Stored values keep the unit they were recorded in; display converts to ranch units. */
export type Dimension =
  | "weight" | "length" | "height" | "volume" | "dose" | "area" | "temperature" | "duration"
  | "distance" | "rate" | "count" | "concentration" | "precipitation" | "any";

export const UNITS: Record<Dimension, readonly string[]> = {
  weight: ["lb", "kg", "ton", "t", "oz", "g"],
  length: ["in", "cm", "ft", "m", "mm"],
  height: ["in", "cm", "hands"],
  volume: ["ml", "cc", "l", "gal", "qt", "fl_oz", "bu"],
  dose: ["ml", "cc", "mg", "g", "mg_per_kg", "ml_per_100lb", "tablet", "bolus", "dose", "straw", "unit"],
  area: ["ac", "ha", "sqft", "sqm"],
  temperature: ["F", "C"],
  duration: ["min", "h", "d", "wk", "mo", "yr"],
  distance: ["mi", "km", "ft", "m"],
  rate: ["lb_per_day", "kg_per_day", "lb_per_head_day", "kg_per_head_day", "gal_per_day"],
  count: ["head", "each", "bale", "straw", "embryo", "bag", "bottle", "dose"],
  concentration: ["pct", "ppm", "mg_per_l"],
  precipitation: ["in", "mm"],
  any: [],
};

/** Short display forms. Translatable words go through the catalog; symbols do not. */
export const UNIT_LABELS: Record<string, string> = {
  lb: "lb", kg: "kg", ton: "ton", t: "t", oz: "oz", g: "g",
  in: "in", cm: "cm", ft: "ft", m: "m", mm: "mm", hands: "hands",
  ml: "mL", cc: "cc", l: "L", gal: "gal", qt: "qt", fl_oz: "fl oz", bu: "bu",
  mg: "mg", mg_per_kg: "mg/kg", ml_per_100lb: "mL/100 lb", tablet: "tablets", bolus: "boluses", dose: "doses", straw: "straws", unit: "units",
  ac: "ac", ha: "ha", sqft: "sq ft", sqm: "m²", F: "°F", C: "°C",
  min: "min", h: "h", d: "days", wk: "weeks", mo: "months", yr: "years", mi: "mi", km: "km",
  lb_per_day: "lb/day", kg_per_day: "kg/day", lb_per_head_day: "lb/hd/day", kg_per_head_day: "kg/hd/day", gal_per_day: "gal/day",
  head: "head", each: "each", bale: "bales", embryo: "embryos", bag: "bags", bottle: "bottles",
  pct: "%", ppm: "ppm", mg_per_l: "mg/L",
};

const TO_BASE: Record<string, [Dimension, number]> = {
  lb: ["weight", 0.45359237], kg: ["weight", 1], g: ["weight", 0.001], oz: ["weight", 0.028349523125], ton: ["weight", 907.18474], t: ["weight", 1000],
  in: ["length", 0.0254], cm: ["length", 0.01], mm: ["length", 0.001], ft: ["length", 0.3048], m: ["length", 1],
  ml: ["volume", 0.001], cc: ["volume", 0.001], l: ["volume", 1], gal: ["volume", 3.785411784], qt: ["volume", 0.946352946], fl_oz: ["volume", 0.0295735295625],
  ac: ["area", 4046.8564224], ha: ["area", 10000], sqft: ["area", 0.09290304], sqm: ["area", 1],
  mi: ["distance", 1609.344], km: ["distance", 1000],
};

/** Convert between compatible units. Returns null when the units are not convertible. */
export function convertUnit(value: number, from: string, to: string): number | null {
  if (from === to) return value;
  if (from === "F" && to === "C") return ((value - 32) * 5) / 9;
  if (from === "C" && to === "F") return (value * 9) / 5 + 32;
  const a = TO_BASE[from];
  const b = TO_BASE[to];
  if (!a || !b) return null;
  // Length and distance share meters.
  const dim = (d: Dimension) => (d === "distance" ? "length" : d);
  if (dim(a[0]) !== dim(b[0])) return null;
  return (value * a[1]) / b[1];
}

export type UnitSystem = "imperial" | "metric";

export function preferredUnit(dimension: Dimension, system: UnitSystem): string | null {
  const imperial: Partial<Record<Dimension, string>> = { weight: "lb", length: "in", height: "in", area: "ac", temperature: "F", distance: "mi", precipitation: "in", volume: "gal", rate: "lb_per_day" };
  const metric: Partial<Record<Dimension, string>> = { weight: "kg", length: "cm", height: "cm", area: "ha", temperature: "C", distance: "km", precipitation: "mm", volume: "l", rate: "kg_per_day" };
  return (system === "metric" ? metric : imperial)[dimension] ?? null;
}
